import { Link } from "react-router-dom";
import { useState } from "react";
import { Instagram, Facebook, Youtube, Mail, ArrowUpRight, Film } from "lucide-react";
import { SOCIALS } from "@/data/site";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.31a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.74z" />
  </svg>
);

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. Welcome to the circle.");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0A0A0A] border-t border-[#262626] mt-20"
    >
      {/* Big outline marquee */}
      <div className="overflow-hidden border-b border-[#262626] py-8">
        <div className="marquee-track">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="outline-text text-[8rem] md:text-[12rem] leading-none whitespace-nowrap px-12 uppercase"
            >
              Prove Your Point ·
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <h3 className="font-display text-4xl md:text-5xl uppercase leading-none mb-4">
              Join <span className="text-[#1E6FE0]">the</span> <span className="text-[#E10600]">conversation</span>
            </h3>
            <p className="text-zinc-400 mb-6 max-w-md">
              New episodes drop Mondays. Get show notes, guest drops & behind-the-scenes
              from the front lines of modern relationships straight to your inbox.
            </p>
            <form
              onSubmit={submit}
              data-testid="newsletter-form"
              className="flex items-end gap-4 max-w-md"
            >
              <input
                data-testid="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="input-edge flex-1"
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                disabled={loading}
                className="btn-primary disabled:opacity-50"
              >
                {loading ? "..." : "Subscribe"}
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </form>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E10600] mb-4">
              Navigate
            </p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-zinc-300 hover:text-white">About The Show</Link></li>
              <li><Link to="/episodes" className="text-zinc-300 hover:text-white">Episodes</Link></li>
              <li><Link to="/be-a-guest" className="text-zinc-300 hover:text-white">Be A Guest</Link></li>
              <li><Link to="/book" className="text-zinc-300 hover:text-white">Book A Slot</Link></li>
              <li><Link to="/merch" className="text-zinc-300 hover:text-white">Merch</Link></li>
              <li><Link to="/media" className="text-zinc-300 hover:text-white">Media & Press</Link></li>
              <li><Link to="/sponsorship" className="text-zinc-300 hover:text-white">Sponsorship</Link></li>
              <li><Link to="/contact" className="text-zinc-300 hover:text-white">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E10600] mb-4">
              Connect
            </p>
            <div className="flex flex-wrap gap-3 mb-6">
              <a data-testid="social-instagram" href={SOCIALS.instagram} target="_blank" rel="noreferrer"
                className="w-11 h-11 border border-[#262626] hover:border-[#E10600] flex items-center justify-center transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a data-testid="social-facebook" href={SOCIALS.facebook} target="_blank" rel="noreferrer"
                className="w-11 h-11 border border-[#262626] hover:border-[#E10600] flex items-center justify-center transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a data-testid="social-youtube" href={SOCIALS.youtube} target="_blank" rel="noreferrer"
                className="w-11 h-11 border border-[#262626] hover:border-[#E10600] flex items-center justify-center transition-colors">
                <Youtube className="w-5 h-5" />
              </a>
              <a data-testid="social-tiktok" href={SOCIALS.tiktok} target="_blank" rel="noreferrer"
                className="w-11 h-11 border border-[#262626] hover:border-[#E10600] flex items-center justify-center transition-colors">
                <TikTokIcon className="w-5 h-5" />
              </a>
              <a
                data-testid="social-imdb"
                href={SOCIALS.imdb}
                target="_blank"
                rel="noreferrer"
                title="IMDb"
                className="w-11 h-11 border border-[#262626] hover:border-[#F5C518] hover:text-[#F5C518] flex items-center justify-center transition-colors"
              >
                <Film className="w-5 h-5" />
              </a>
            </div>
            <a
              href={`mailto:${SOCIALS.email}`}
              data-testid="footer-email"
              className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-[#E10600]"
            >
              <Mail className="w-4 h-4" />
              {SOCIALS.email}
            </a>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-[#262626] flex flex-col md:flex-row justify-between gap-4">
          <p className="text-xs text-zinc-500 font-mono">
            © {new Date().getFullYear()} Prove Your Point Podcast. All rights reserved.
          </p>
          <p className="text-xs text-zinc-500 font-mono uppercase tracking-widest">
            Hosted by Manuel Santana · Streaming Mondays 7-9PM
          </p>
        </div>
      </div>
    </footer>
  );
}
